import ReportIcon from "@uui/File03";
import { isEmpty } from "lodash/fp";

import { Typography } from "@mui/material";

import Table from "@app/analytics/components/Table";
import TableBody from "@app/analytics/components/Table/TableBody";
import TableCell from "@app/analytics/components/Table/TableCell";
import TableHead from "@app/analytics/components/Table/TableHead";
import TableRow from "@app/analytics/components/Table/TableRow";
import useDataTable, { HeadCell } from "@app/common/hooks/useDataTable";
import { CoachingSession } from "@app/common/types";
import { formatStartAndEndTime } from "@app/common/utils";
import { pluralizePhrase } from "@app/common/utils";

import {
  EmptyStateContainer,
  EmptyStateSubtext,
  EmptyStateIcon,
} from "@rui/EmptyStateContainer";
import RecurringMeetingBadge from "@rui/RecurringMeetingBadge";
import { Column, Row } from "@rui/Stack";

const COMMON_HEAD_CELLS: HeadCell<CoachingSession>[] = [
  {
    id: "meeting",
    label: "Meeting",
    sortBy: (d) => d.title.toLowerCase(),
  },
  {
    id: "date",
    label: "Date & Time",
    sortBy: (d) => d.startTime.toMillis(),
  },
  {
    id: "talkTimePercent",
    label: "Talk Time",
    sortBy: (d) => d.talkTimePercent.value,
  },
];

interface CoachingTableProps {
  defaultOrderBy?: keyof CoachingSession;
  defaultOrder?: "asc" | "desc";
  isLoading: boolean;
  renderSectionCells: (data: CoachingSession) => React.ReactNode;
  rows: CoachingSession[];
  sectionHeadCells: HeadCell<CoachingSession>[];
}

const CoachingTable = ({
  defaultOrderBy,
  defaultOrder,
  isLoading,
  renderSectionCells,
  rows,
  sectionHeadCells,
}: CoachingTableProps) => {
  const combinedHeadCells = [...COMMON_HEAD_CELLS, ...sectionHeadCells];

  const { order, orderBy, handleSort, tableRows } = useDataTable(
    rows,
    combinedHeadCells,
    {
      defaultOrderBy: defaultOrderBy ?? "rate",
      defaultOrder: defaultOrder ?? "desc",
    }
  );

  return (
    <Column sx={{ overflowX: "auto" }}>
      <Table sx={{ bgcolor: "#FFF" }}>
        <TableHead
          headCells={combinedHeadCells}
          onSort={handleSort}
          order={order}
          orderBy={orderBy}
        />
        <TableBody loading={isLoading}>
          {isEmpty(tableRows) ? (
            <TableRow>
              <TableCell colSpan={combinedHeadCells.length}>
                <EmptyStateContainer sx={{ minHeight: "100px" }}>
                  <EmptyStateIcon Icon={ReportIcon} />
                  <EmptyStateSubtext>No meeting reports, yet</EmptyStateSubtext>
                </EmptyStateContainer>
              </TableCell>
            </TableRow>
          ) : (
            tableRows.map((session) => (
              <TableRow key={session.sessionId}>
                <TableCell sx={{ maxWidth: "300px" }}>
                  <Column>
                    <Typography
                      variant="bodySecondaryReg"
                      sx={{
                        color: "shades.neutral.800",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {session.title}
                    </Typography>
                    <Typography
                      variant="labelReg"
                      sx={{ color: "shades.neutral.500" }}
                    >
                      {pluralizePhrase(session.participants, "participant")}
                    </Typography>
                  </Column>
                </TableCell>
                <TableCell>
                  <Column justifyContent="center" sx={{ minHeight: "40px" }}>
                    <Row variant="left" spacing={1}>
                      <Typography variant="bodySecondaryReg">
                        {session.startTime.toFormat("EEE M/d/yy")}
                      </Typography>
                      {session.isRecurring && <RecurringMeetingBadge />}
                    </Row>
                    <Typography
                      variant="labelReg"
                      sx={{ color: "shades.neutral.500" }}
                    >
                      {formatStartAndEndTime(
                        session.startTime,
                        session.endTime
                      )}
                    </Typography>
                  </Column>
                </TableCell>
                <TableCell>{session.talkTimePercent.formattedValue}</TableCell>
                {renderSectionCells(session)}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </Column>
  );
};

export default CoachingTable;
